import React, {
  useContext,
  createContext,
  ReactNode,
  useReducer,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { error } from '@mummssoftware/utils/constants';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import {
  VisitsGlobalContext,
  GlobalContext,
  SET_PATIENT_VISITS,
  SET_FILTER_ATTRIBUTES,
  SET_FILTERED_VISITS,
  SET_TRAVEL_TIME,
  SET_TRANSIT_FEES,
  SET_TOLLS_FEES,
  SET_MILEAGE,
  SET_TIME,
  SET_EDIT_TIME,
  SET_CC_TIME,
  SET_START_TIME,
  SET_EDITING_PAST_VISIT,
} from '@mummssoftware/global-context';
import {
  attributeIdsForVisitFetch,
  attributeMatchModeForVisitFetch,
  fetchPimreaderVisitsForPatient,
  getVisitByPersonAndVisitIdV2,
} from '@mummssoftware/utils/pimReader';
import {
  SET_GRID_API,
  SET_API_ERROR,
  SET_EDITING_ROW,
  SET_ATTRIBUTES,
  TOGGLE_FACE_TO_FACE,
  POST_VISIT_EDITS,
  SET_SHOW_LOADER,
  ADD_DETAILS,
  REMOVE_DETAILS,
  SET_DATE_ERROR,
  CHANGE_DETAIL,
  DID_EDIT_VISIT,
} from './constants';
import {
  getFilterableAttributes,
  filterVisitsByAttribute,
  getFilteredAttributes,
  toggleAttributesColumn,
  formatAttributesCamel,
} from './visitLogUtils';
import { AppContext } from '../../context';
import defaults from '../../translations/en.json';

type VisitLogContextProviderProps = {
  children: ReactNode;
};

type initFunc = () => null;
const initFunction = () => null;

type VisitLogContext = {
  editingRow: string | null;
  editingAttributes: any | null;
  toggleF2F: boolean | null;
  showLoader: boolean;
  postVisitEdits: boolean;
  gridAPI: null | any;
  APIError: any;
  dispatch?: any;
  detailsCache: any;
  isVisitEdited: boolean;
  fetchVisitDetails: (visit: any) => mumms.pimreaderVisit | any;
  resetUI: () => any;
  resetVisitAttributes: () => any;
  dateError: boolean;
  changeDetail: boolean;
  /** HOPE patient status from workflow; only populated when not `smallWidget`. */
  hopePatientData: mumms.JSONObject | null;
};

type VisitLogContextState = VisitLogContext;

const initVisitLogContext = {
  editingRow: null,
  editingAttributes: null,
  toggleF2F: null,
  showLoader: false,
  postVisitEdits: false,
  isVisitEdited: false,
  APIError: null,
  gridAPI: null,
  detailsCache: {},
  fetchVisitDetails: () => ({} as any),
  resetUI: () => ({} as any),
  resetVisitAttributes: () => ({} as any),
  dateError: false,
  changeDetail: false,
  hopePatientData: null,
};

const initialState = initVisitLogContext;

export const VisitLogContextReducer = (
  state: VisitLogContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_API_ERROR:
      newState.APIError = action.payload;
      break;
    case SET_GRID_API:
      newState.gridAPI = action.payload;
      break;
    case SET_EDITING_ROW:
      newState.editingRow = action.payload;
      break;
    case SET_ATTRIBUTES:
      newState.editingAttributes = formatAttributesCamel(action.payload);
      break;
    case POST_VISIT_EDITS:
      newState.postVisitEdits = action.payload;
      break;
    case TOGGLE_FACE_TO_FACE:
      newState.toggleF2F = action.payload;
      break;
    case SET_SHOW_LOADER:
      newState.showLoader = action.payload;
      break;
    case SET_DATE_ERROR:
      newState.dateError = action.payload;
      break;
    case CHANGE_DETAIL:
      newState.changeDetail = action.payload;
      break;
    case DID_EDIT_VISIT:
      newState.isVisitEdited = action.payload;
      break;
    case ADD_DETAILS:
      {
        const { id } = action.payload;
        const newDetails = { ...state.detailsCache };

        newDetails[id] = action.payload;
        newState.detailsCache = newDetails;
      }
      break;
    case REMOVE_DETAILS:
      {
        const newDetails = { ...state.detailsCache };
        delete newDetails[action.payload];
        newState.detailsCache = newDetails;
      }
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const VisitLogContext = createContext<VisitLogContext>(
  initVisitLogContext as VisitLogContext,
);
export const VisitLogContextProvider = ({
  children,
}: VisitLogContextProviderProps) => {
  const [
    {
      gridAPI,
      APIError,
      editingRow,
      editingAttributes,
      toggleF2F,
      showLoader,
      postVisitEdits,
      detailsCache,
      dateError,
      changeDetail,
      isVisitEdited,
    },
    dispatch,
  ] = useReducer(VisitLogContextReducer, initialState);

  const [
    hopePatientData,
    setHopePatientData,
  ] = useState<mumms.JSONObject | null>(null);

  const {
    keycloak,
    siteId,
    agency,
    patient,
    smallWidget,
    currentUser,
    workflow,
  } = useContext(AppContext);

  const {
    patientVisitHistory,
    dispatchVG,
    refreshVisits,
    providerInfo,
    filteringAttributes,
    fromDate,
    toDate,
    filterAttributes,
    minDurationHH,
    minDurationMM,
    chartAfterDD,
    chartAfterHH,
    summaryAttributeIds,
    summaryAttributeMatchMode,
  } = useContext(VisitsGlobalContext);
  const globalContext = useContext(GlobalContext);

  const loadHopePatientData = useCallback(async () => {
    if (smallWidget) {
      setHopePatientData(null);
      return;
    }
    const hopeStatusRes = await workflow.getHopePatientStatus(
      `${patient?.id}` || '',
      `${(patient?.episodes?.length || 1) - 1}`,
      agency || '',
      siteId || '',
    );
    const hopeStatus =
      !(hopeStatusRes instanceof Response) && hopeStatusRes.length
        ? hopeStatusRes[0]
        : null;
    setHopePatientData(
      hopeStatus != null &&
        typeof hopeStatus === 'object' &&
        !Array.isArray(hopeStatus)
        ? (hopeStatus as mumms.JSONObject)
        : null,
    );
  }, [smallWidget, agency, siteId, patient?.id, patient?.episodes?.length]);

  /** Full visit log only: on mount and when visit list or refresh flag changes — not on every date/filter-driven `getInitData`. */
  useEffect(() => {
    if (smallWidget) {
      setHopePatientData(null);
      return;
    }

    loadHopePatientData();
  }, [smallWidget, refreshVisits]);

  const getInitData = async () => {
    const visits = await fetchPimreaderVisitsForPatient(
      keycloak,
      agency,
      patient,
      providerInfo,
      fromDate,
      toDate,
      minDurationHH,
      minDurationMM,
      chartAfterDD,
      chartAfterHH,
      attributeIdsForVisitFetch(
        patient,
        filteringAttributes,
        summaryAttributeIds,
      ),
      attributeMatchModeForVisitFetch(patient, summaryAttributeMatchMode),
    );

    if (visits instanceof Response) {
      dispatch({
        type: SET_API_ERROR,
        payload: {
          text: defaults.pimreaderFailed,
          severity: error,
        },
      });
    } else {
      dispatchVG({
        type: SET_PATIENT_VISITS,
        payload: visits,
      });
      dispatchVG({
        type: SET_FILTER_ATTRIBUTES,
        payload: getFilterableAttributes(visits),
      });
    }
  };

  const fetchVisitDetails = async (
    visit: mumms.Visit | mumms.pimreaderVisit,
  ) => {
    if (keycloak) {
      const res = await getVisitByPersonAndVisitIdV2(
        visit.personnumber,
        String(visit.id),
        agency,
        keycloak,
      );
      dispatch({
        type: ADD_DETAILS,
        payload: res && res[0],
      });
    }
  };

  /*
   * TODO: Derive filteredVisits with useMemo instead of dispatching SET_FILTERED_VISITS
   * in this effect. This effect runs after commit, so VisitLogTable briefly shows the
   * full patientVisitHistory (filteredVisits || patientVisitHistory) before the filtered
   * list arrives — same flash as HOPE batch mode before useMemo (apps/hope HopeWidgetContext).
   *
   * Refactor:
   * 1. useMemo(filterVisitsByAttribute(...)) from filteringAttributes, filterAttributes,
   *    patientVisitHistory; expose on VisitLogContext (stop writing SET_FILTERED_VISITS).
   * 2. Keep a separate useEffect only for toggleAttributesColumn(gridAPI, ...).
   * 3. In VisitLogTable: rowData = filteringAttributes ? filteredVisits : patientVisitHistory
   *    (no || fallback to full history while filtering).
   */
  // update filtered list as user selects from ui
  useEffect(() => {
    if (filteringAttributes) {
      toggleAttributesColumn(gridAPI, true);
      dispatchVG({
        type: SET_FILTERED_VISITS,
        payload: filterVisitsByAttribute(
          patientVisitHistory,
          getFilteredAttributes(filterAttributes, filteringAttributes),
        ),
      });
    } else {
      toggleAttributesColumn(gridAPI, false);
      dispatchVG({
        type: SET_FILTERED_VISITS,
        payload: null,
      });
    }
  }, [
    filteringAttributes,
    filterAttributes,
    refreshVisits,
    patientVisitHistory,
  ]);

  // if the widget is opened through visit summary...
  useEffect(() => {
    if (!patient && providerInfo) {
      resetUI();
      dispatchVG({
        type: SET_PATIENT_VISITS,
        payload: null,
      });
      getInitData();
    }
  }, [
    providerInfo?.personnumber,
    providerInfo?.siteoffice,
    fromDate,
    toDate,
    filteringAttributes,
    summaryAttributeIds,
    summaryAttributeMatchMode,
  ]);

  // fetch initial list of patient Visits
  useEffect(() => {
    if (!patientVisitHistory && smallWidget && patient) getInitData();
  }, [
    patientVisitHistory,
    refreshVisits,
    minDurationHH,
    minDurationMM,
    chartAfterDD,
    chartAfterHH,
  ]);

  useEffect(() => {
    if (
      !smallWidget &&
      (minDurationHH ||
        minDurationMM ||
        fromDate ||
        toDate ||
        chartAfterDD ||
        chartAfterHH)
    )
      getInitData();
  }, [
    minDurationHH,
    minDurationMM,
    fromDate,
    toDate,
    chartAfterDD,
    chartAfterHH,
  ]);

  useEffect(() => {
    if (refreshVisits) {
      getInitData();
    }
  }, [refreshVisits]);

  useEffect(() => {
    if (editingRow) {
      const visit = patientVisitHistory?.find(
        (v: mumms.pimreaderVisit) => String(v.id) === editingRow,
      );
      dispatch({
        type: TOGGLE_FACE_TO_FACE,
        payload: visit?.facetofacevisit ?? null,
      });
    } else {
      dispatch({
        type: SET_ATTRIBUTES,
        payload: null,
      });
      dispatch({
        type: TOGGLE_FACE_TO_FACE,
        payload: null,
      });
    }
  }, [editingRow]);

  const resetVisitGlobalValues = () => {
    dispatchVG({
      type: SET_TRANSIT_FEES,
      payload: null,
    });
    dispatchVG({
      type: SET_TRAVEL_TIME,
      payload: null,
    });
    dispatchVG({
      type: SET_TOLLS_FEES,
      payload: null,
    });
    dispatchVG({
      type: SET_MILEAGE,
      payload: null,
    });
    dispatchVG({
      type: SET_TIME,
      payload: ['00', '00'],
    });
    dispatchVG({
      type: SET_EDIT_TIME,
      payload: null,
    });
    dispatchVG({
      type: SET_CC_TIME,
      payload: null,
    });
    dispatchVG({
      type: SET_START_TIME,
      payload: null,
    });
    globalContext.dispatch({
      type: SET_EDITING_PAST_VISIT,
      payload: null,
    });
  };

  const resetUI = () => {
    dispatch({
      type: SET_EDITING_ROW,
      payload: null,
    });

    dispatch({
      type: TOGGLE_FACE_TO_FACE,
      payload: null,
    });
    dispatch({
      type: DID_EDIT_VISIT,
      payload: false,
    });
    resetVisitAttributes();
    resetVisitGlobalValues();
    dispatch({
      type: SET_SHOW_LOADER,
      payload: false,
    });
    dispatch({
      type: SET_DATE_ERROR,
      payload: false,
    });
    gridAPI && gridAPI.redrawRows();
  };

  const resetVisitAttributes = () => {
    dispatch({
      type: TOGGLE_FACE_TO_FACE,
      payload: null,
    });
    dispatch({
      type: DID_EDIT_VISIT,
      payload: false,
    });
    dispatch({
      type: POST_VISIT_EDITS,
      payload: false,
    });
    dispatch({
      type: CHANGE_DETAIL,
      payload: false,
    });
  };

  const context = useMemo(
    () => ({
      editingRow,
      editingAttributes,
      filterAttributes,
      toggleF2F,
      showLoader,
      postVisitEdits,
      APIError,
      gridAPI,
      dispatch,
      detailsCache,
      fetchVisitDetails,
      resetUI,
      isVisitEdited,
      resetVisitAttributes,
      dateError,
      changeDetail,
      hopePatientData,
    }),
    [
      APIError,
      gridAPI,
      editingRow,
      editingAttributes,
      filterAttributes,
      toggleF2F,
      showLoader,
      postVisitEdits,
      dispatch,
      patientVisitHistory,
      refreshVisits,
      detailsCache,
      fetchVisitDetails,
      resetUI,
      isVisitEdited,
      resetVisitAttributes,
      dateError,
      changeDetail,
      hopePatientData,
    ],
  );

  return (
    <VisitLogContext.Provider value={context}>
      {children}
    </VisitLogContext.Provider>
  );
};

export default VisitLogContextProvider;
